import {
  IBuilder,
  ICohortDurationStart,
  ICohortRow,
  IFunnelBuilder,
  ILookForwardWindow,
  IRow,
  IRowMeta,
  LookForwardWindowRequirement,
  RowType,
} from './types';
import { isNil } from 'lodash';
import { assertCondition, assertNotNil } from '../../../utils/types';
import {
  AttributionKey,
  FunnelModeKey,
  FunnelOrderKey,
  IDateRangeFieldsFragment,
  IFilterFieldsFragment,
  IFunnelCohortAnalysisQueryFieldsFragment,
  IRetentionCohortAnalysisQueryFieldsFragment,
  IVirtualEventFieldsFragment,
  MeasureType,
  QueryType,
  RetentionCohortOperatorKey,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../../utils/interfaces';
import { Draft, freeze } from '@reduxjs/toolkit';
import { DeepReadonly } from 'ts-essentials';
import { isDayPartition } from '../../../components/Builder/Partition/utils';
import { localDateToMoment } from '../../../components/Builder/DatePickerWidget/utils';

export const cohortRowMetaTypes = [
  RowType.globalCohorts,
  RowType.breakdownCohorts,
  RowType.predictionCohorts,
  RowType.builderCohorts,
];

/* ============================= Reducer Action Functions ======================================== */

/**
 * Helper function to distinct Funnel Row: is it regular or negative step
 * @param rowMeta
 */
const isNegativeStepRow = (
  rowMeta: IRowMeta,
): rowMeta is {
  rowType: RowType.steps;
  rowIndex: number;
  negativeStepRowIndex: number;
} => {
  const { rowType, negativeStepRowIndex } = rowMeta;
  return (
    rowType === RowType.steps &&
    typeof negativeStepRowIndex === 'number' &&
    negativeStepRowIndex >= 0
  );
};

const rowTypesWithEventConditionsCollection = new Set<RowType>([
  RowType.steps, // applicable for regular steps, not applicable for negative steps
  RowType.measures,
  // RowType.global, // not applicable, this is just filter
  RowType.paths,
  RowType.retention,
  // RowType.prediction, // single event
  // RowType.trigger, // not applicable
  RowType.breakdownCohorts,
  RowType.globalCohorts,
  RowType.builderCohorts,
  RowType.predictionCohorts,
  RowType.mergeEvents,
  // RowType.expandEvents, // single event
  // RowType.foldEvents, // single event
  // RowType.footer, // not applicable
]);

/**
 * Returns true if the specified row allows multiple event conditions, false
 * otherwise
 */
export const isEventConditionsCollection = (rowMeta: IRowMeta) =>
  rowTypesWithEventConditionsCollection.has(rowMeta.rowType) &&
  // special case for negative steps
  !isNegativeStepRow(rowMeta);

export const canExpandVirtualEvent = (
  virtualEvent: MaybeNil<IVirtualEventFieldsFragment>,
  rowMeta: IRowMeta,
): virtualEvent is IVirtualEventFieldsFragment => {
  const eventsCount = virtualEvent?.eventsConditions?.length ?? 0;
  return (
    // virtual event has just one basic event, can be expanded everywhere we have event
    eventsCount === 1 ||
    // virtual event has > 1 events, thus can be expanded only in places where we support
    // multiple (eventCondition collection) events, but only when the virtual event is in the
    // first group (to avoid overflowing the limitation)
    (eventsCount > 1 &&
      isEventConditionsCollection(rowMeta) &&
      !isNil(rowMeta.eventIndex) &&
      rowMeta.eventIndex === 0)
  );
};

export const selectEventConditionsCollection = (
  state: DeepReadonly<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, rowIndex } = rowMeta;

  if (rowType === RowType.paths) {
    return state[rowType][rowIndex];
  } else if (rowType === RowType.retention) {
    return state[rowType].retentionConditions[rowIndex];
  } else {
    return selectRow(state, rowMeta)?.eventConditions;
  }
};

export const selectEventCondition = (
  draft: DeepReadonly<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, rowIndex, eventIndex } = rowMeta;

  // Special case for RowType.steps
  if (isNegativeStepRow(rowMeta)) {
    return draft[rowType][rowIndex].negativeSteps[rowMeta.negativeStepRowIndex];
  } else if (
    rowType === RowType.expandEvents ||
    rowType === RowType.foldEvents
  ) {
    return draft[rowType][rowIndex].eventCondition;
  } else if (rowType === RowType.prediction) {
    return draft[rowType].eventCondition;
  } else if (rowType === RowType.trigger) {
    return draft.trigger;
  } else {
    assertNotNil(eventIndex);
    return selectEventConditionsCollection(draft, rowMeta)?.[eventIndex];
  }
};

export const selectEventFilterCondition = (
  state: DeepReadonly<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, eventIndex } = rowMeta;

  let eventCondition;
  let filter;

  if (
    (cohortRowMetaTypes.includes(rowType) || isMeasureCohortFilter(rowMeta)) &&
    isNil(eventIndex)
  ) {
    const draftRow = selectRow(state, rowMeta);

    filter = draftRow.conditionFilter;
  } else if (rowType === RowType.global) {
    filter = state.filter;
  } else if (rowType === RowType.trigger) {
    filter = state?.trigger?.filter ?? null;
  } else {
    eventCondition = selectEventCondition(state, rowMeta);
    filter = eventCondition?.filter;
  }
  return { eventCondition, filter };
};

export const removeNegativeStepsByOrder = (draft: Draft<IBuilder>) => {
  const selectedOptions = draft.selectedOptions;

  assertCondition(selectedOptions?.type === QueryType.Funnel);

  const funnelOrder = selectedOptions.funnelOrder;
  if (funnelOrder === FunnelOrderKey.Sequence) {
    return;
  }
  for (let i = 0; i < draft.steps.length; i++) {
    if (
      funnelOrder !== FunnelOrderKey.Custom ||
      (i > 0 && draft.stepOrders[i - 1] !== FunnelOrderKey.Sequence)
    ) {
      draft.steps[i].negativeSteps = [];
    }
  }
};

export const resetNamedMeasureFields = (
  draft: Draft<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, rowIndex } = rowMeta;
  if (rowIndex < 0) {
    return;
  }

  const isMeasureRow = rowType === RowType.measures;

  // Reset named compound measure fields in Query builder
  if (isMeasureRow && isNil(rowMeta.cohortRowType)) {
    resetCompoundMeasure(draft);
  }

  if (
    cohortRowMetaTypes.includes(rowType) ||
    isMeasureCohortFilter(rowMeta) ||
    isMeasureRow
  ) {
    const row = selectRow(draft, rowMeta);
    row.measureId = '';
    row.isCollapsedMeasure = false;
    row.showMeasure = false;
    row.type = MeasureType.Simple;
    row.defaultNamedMeasure = null;
  }
};

/**
 * Until KB-4341, we allowed users to set aliases when building the query. Caprica still loads and passes back
 * original names of analyses created prior KB-4341 to retain these aliases. But if you update the query, these
 * aliases are reset to the default via this function because they may not longer be applicable to the new definition
 */
export const resetBuilderAliases = (
  draft: Draft<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, rowIndex } = rowMeta;
  if (rowIndex < 0) {
    return;
  }
  const row = draft[rowType][rowIndex];
  if (
    rowType === RowType.breakdownCohorts ||
    rowType === RowType.globalCohorts ||
    rowType === RowType.builderCohorts ||
    !row ||
    (rowType !== RowType.measures && rowType !== RowType.steps)
  ) {
    return;
  }

  // expression alias (if it is complex measure)
  delete draft.alias;
  // current measure/step alias / generated name
  delete row.alias;
  delete row.name;
};

export const resetNamedCohortFields = (
  draft: Draft<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, rowIndex } = rowMeta;

  if (rowIndex < 0) {
    return;
  }

  if (cohortRowMetaTypes.includes(rowType) || isMeasureCohortFilter(rowMeta)) {
    const row = selectRow(draft, rowMeta);

    row.cohortId = '';
    row.showCohort = false;
    row.defaultNamedCohort = null;

    if (rowType === RowType.builderCohorts) {
      resetBuilderSampledCohort(draft);
    }
  }
};

export const resetBuilderSampledCohort = (draft: Draft<IBuilder>) => {
  draft.builderSampledCohort.id = '';
  draft.builderSampledCohort.name = '';
};

export const funnelAvailableOrders = (state: DeepReadonly<IFunnelBuilder>) => {
  const { defaultOptions, selectedOptions } = state;

  // intersect funnel orders with orders from the subject
  const subjectOrderKeys =
    defaultOptions.subjects
      ?.find((subject) => subject.key === selectedOptions.subject)
      ?.funnelOrders?.map((fo) => fo.key) ?? [];

  return (
    defaultOptions.funnelOrders?.filter((order) =>
      subjectOrderKeys.includes(order.key),
    ) ?? []
  );
};

// determines if we need to access cohorts within a nested array of 2 levels deep
export const isMeasureCohortFilter = (
  rowMeta: IRowMeta,
): rowMeta is {
  rowType: RowType.measures | RowType.dataTableMeasures;
  rowIndex: number;
  cohortRowType: 'cohortFilter';
} => {
  const { rowType, cohortRowType } = rowMeta;

  return (
    (rowType === RowType.measures || rowType === RowType.dataTableMeasures) &&
    cohortRowType === 'cohortFilter'
  );
};

export const selectRow = (draft: DeepReadonly<IBuilder>, rowMeta: IRowMeta) => {
  const { rowType, rowIndex } = rowMeta;
  if (isMeasureCohortFilter(rowMeta)) {
    return draft[rowType][rowIndex][rowMeta.cohortRowType];
  }
  return draft[rowType][rowIndex];
};

export const replaceRow = (
  draft: Draft<IBuilder>,
  rowMeta: IRowMeta,
  produceNewRow: () => IRow | ICohortRow,
) => {
  const { rowType, rowIndex } = rowMeta;
  const newRow = produceNewRow();
  if (isMeasureCohortFilter(rowMeta)) {
    draft[rowType][rowIndex][rowMeta.cohortRowType] = newRow;
  } else {
    draft[rowType][rowIndex] = newRow;
  }
};

export const selectErrorRow = (
  state: DeepReadonly<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, rowIndex } = rowMeta;
  if (isMeasureCohortFilter(rowMeta)) {
    return state?.errors?.[rowType]?.[rowIndex]?.[rowMeta.cohortRowType];
  }
  return state?.errors?.[rowType]?.[rowIndex];
};

export const shouldResetMeasure = (rowMeta: IRowMeta) => {
  // do not allow reset measure name on cohort condition filter
  // in the Cohort Sample page
  return !(
    rowMeta.rowType === RowType.builderCohorts && isNil(rowMeta.eventIndex)
  );
};

// selects the appropriate parent state for measure filters & result filter
export const selectMeasureFilterRow = (
  state: DeepReadonly<IBuilder>,
  rowMeta: IRowMeta,
) => {
  const { rowType, rowIndex } = rowMeta;

  if (rowType === RowType.global) {
    // indicates a resultFilter on builder.
    return state;
  } else {
    // indicates a measureFilter on measures
    return state[rowType][rowIndex];
  }
};

export const replaceFilter = (
  draft: Draft<IBuilder>,
  rowMeta: IRowMeta,
  filter: IFilterFieldsFragment | null,
) => {
  const { rowType, eventIndex } = rowMeta;

  if (rowType === RowType.trigger && draft.trigger) {
    if (filter) {
      draft.trigger.filter = filter;
    } else {
      draft.trigger.filter = null;
    }
    return;
  }

  if (rowType === RowType.global) {
    if (filter) {
      draft.filter = filter;
    } else {
      delete draft.filter;
    }
    return;
  }

  if (
    (cohortRowMetaTypes.includes(rowType) || isMeasureCohortFilter(rowMeta)) &&
    isNil(eventIndex)
  ) {
    const draftRow = selectRow(draft, rowMeta);

    if (filter) {
      draftRow.conditionFilter = filter;
    } else {
      delete draftRow.conditionFilter;
    }
    return;
  }

  const eventCondition = selectEventCondition(draft, rowMeta);

  if (eventCondition) {
    if (filter) {
      eventCondition.filter = filter;
    } else {
      delete eventCondition.filter;
    }
  }
};

export const resetErrors = (draft: Draft<IBuilder>) => {
  draft.errors = {};
};

export const resetCompoundMeasure = (draft: Draft<IBuilder>) => {
  draft.compoundMeasureId = null;
  draft.compoundMeasureName = null;
};

const isCohortDurationStartRequired = (
  cohortAnalysisQuery:
    | IFunnelCohortAnalysisQueryFieldsFragment
    | IRetentionCohortAnalysisQueryFieldsFragment,
) => {
  if (cohortAnalysisQuery.__typename === 'RetentionQuery') {
    // Retention - Attribution is First
    if (cohortAnalysisQuery.attribution?.key === AttributionKey.First) {
      return true;
    }
  } else if (cohortAnalysisQuery.__typename === 'FunnelQuery') {
    // Funnel - Partition *is not* Day
    const containsDayPartition = cohortAnalysisQuery.partitions.some((p) =>
      isDayPartition(p.partition),
    );

    if (!containsDayPartition) {
      return true;
    }
  }

  return false;
};

export const defaultCohortDurationStart: ICohortDurationStart = freeze({
  required: false,
});

export const parseCohortDurationStartDate = (
  query:
    | IFunnelCohortAnalysisQueryFieldsFragment
    | IRetentionCohortAnalysisQueryFieldsFragment,
  date: MaybeNil<string>,
): ICohortDurationStart => {
  if (isCohortDurationStartRequired(query)) {
    return { required: true, date };
  } else {
    return defaultCohortDurationStart;
  }
};

const isFunnelLookForwardWindowRequired = (
  cohortAnalysisQuery: IFunnelCohortAnalysisQueryFieldsFragment,
) => {
  const containsDayPartition = cohortAnalysisQuery.partitions.some((p) =>
    isDayPartition(p.partition),
  );

  return !containsDayPartition;
};

export const defaultLookForwardWindow: ILookForwardWindow = freeze({
  required: LookForwardWindowRequirement.NotRequired,
});

export const parseFunnelLookForwardWindow = (
  query: IFunnelCohortAnalysisQueryFieldsFragment,
  lookForwardWindow: MaybeNil<number>,
): ILookForwardWindow => {
  if (
    isFunnelLookForwardWindowRequired(query) &&
    query.mode?.key !== FunnelModeKey.TimeToConvert
  ) {
    return {
      required: LookForwardWindowRequirement.Required,
      windowLength: lookForwardWindow,
      unit: TimeUnit.Day,
    };
  } else {
    return defaultLookForwardWindow;
  }
};

const retentionLookForwardWindowOperators = freeze([
  RetentionCohortOperatorKey.RetainedSince,
  RetentionCohortOperatorKey.NotRetainedSince,
  RetentionCohortOperatorKey.Retained,
  RetentionCohortOperatorKey.NotRetained,
  RetentionCohortOperatorKey.Returned,
]);

export const parseRetentionLookForwardWindow = (
  windowTimeUnit: TimeUnit,
  lookForwardWindow: MaybeNil<number>,
): ILookForwardWindow => {
  return {
    required: LookForwardWindowRequirement.OnRetentionOperators,
    operators: retentionLookForwardWindowOperators,
    windowLength: lookForwardWindow,
    unit: windowTimeUnit,
  };
};

export const isRetentionLookForwardWindowInputRequired = <
  T extends ILookForwardWindow,
>(
  lookForwardWindow: T,
  operator: RetentionCohortOperatorKey,
  isCustomCohortDateRange: boolean,
): lookForwardWindow is T & {
  required: LookForwardWindowRequirement.OnRetentionOperators;
} => {
  return (
    isCustomCohortDateRange &&
    lookForwardWindow.required ===
      LookForwardWindowRequirement.OnRetentionOperators &&
    lookForwardWindow.operators.includes(operator)
  );
};

export const isFunnelLookForwardWindowInputRequired = <
  T extends ILookForwardWindow,
>(
  lookForwardWindow: T,
  isCustomCohortDateRange: boolean,
): lookForwardWindow is T & {
  required: LookForwardWindowRequirement.Required;
} => {
  return (
    isCustomCohortDateRange &&
    lookForwardWindow.required === LookForwardWindowRequirement.Required
  );
};

// updates the actual absolute cohort date range and makes sure that
// the cohort duration start is always before or equal to the
// start of the range
export const updateCohortDateRange = (
  draftCohort: Draft<ICohortRow>,
  dateRange: MaybeNil<IDateRangeFieldsFragment>,
) => {
  draftCohort.contextInput.dateRange = dateRange ?? null;

  if (
    !isNil(dateRange) &&
    draftCohort.cohortDurationStart.required &&
    !isNil(draftCohort.cohortDurationStart.date)
  ) {
    const cohortDurationStartDateMoment = localDateToMoment(
      draftCohort.cohortDurationStart.date,
    );
    const startMoment = localDateToMoment(dateRange.start);
    if (cohortDurationStartDateMoment.isAfter(startMoment, 'date')) {
      draftCohort.cohortDurationStart.date = dateRange.start;
    }
  }
};
